import { useEffect, useState } from 'react';
import {
    Box, Modal, Typography, IconButton, List, ListItem,
    ListItemButton, ListItemText, Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { FETCH_CLIENT_INSPECTIONS_FORM_TEMPLATES_2 } from '../../constant/Apipath';
import { Postrequestcall } from '../../request/Postrequest';
import { useSelector } from 'react-redux';
import { SectionViewModal } from './SectionViewModal';
import { ClientEnglishFallback } from '../constant/ClientEnglishFallback';

export default function NewInspectionModal({ open, onClose, getList }) {
    const [isInspectionLoading, setIsInspectionLoading] = useState(false);
    const [inspectionFormTemplateList, setInspectionFormTemplateList] = useState([]);
    const [openSectionModal, setOpenSectionModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState({});
    const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);

    const accentColor = selectedClientData?.AccentColor || '#4F6EF7';

    const LANGUAGE = ClientEnglishFallback.ClientInspections(multiLanguageData?.ClientInspections || {});
    const { TEXT_NEW_INSPECTION } = LANGUAGE;

    const fetchClientInspections = async () => {
        setIsInspectionLoading(true);
        try {
            const requestPayload = {
                PageNr: 1,
                NrOfRecPerPage: 100,
                IncludeRecordNr: true,
                SearchList: [{ ClientId: selectedClientData?.ClientId }],
                SortList: [{ FieldName: 'id', Direction: 'DESC' }],
                FetchAllowedRecordsOnly: true,
                DoNotSearchInSystemFields: true,
            };
            const res = await Postrequestcall(
                FETCH_CLIENT_INSPECTIONS_FORM_TEMPLATES_2,
                requestPayload,
                loginData?.accessToken
            );
            if (res?.data?.data && res?.data?.status === true && res?.data?.message === 'OK') {
                setInspectionFormTemplateList(res?.data?.data);
            }
        } catch (error) {
            console.log('FETCH_CLIENT_INSPECTIONS_FORM_TEMPLATES_2 [ERROR] : ', error);
        } finally {
            setIsInspectionLoading(false);
        }
    };

    const handleSectionClick = (form) => {
        setSelectedItem({ id: 0, formId: form?.id, formName: form?.title, isSubmit: false });
        setOpenSectionModal(true);
    };

    const handleClose = () => {
        setInspectionFormTemplateList([]);
        onClose();
    };

    useEffect(() => {
        if (open) fetchClientInspections();
    }, [open]);

    return (
        <>
            <Modal open={open} onClose={handleClose}>
                <Box
                    className="client-modal-responsive"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '92%', sm: 460 },
                        maxHeight: '82vh',
                        bgcolor: '#fff',
                        borderRadius: '24px',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.14)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        outline: 'none',
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            px: 3,
                            pt: 3,
                            pb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #F0F2F5',
                            flexShrink: 0,
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                                sx={{ color: '#1A1D23', letterSpacing: '-0.02em', fontSize: '1.05rem' }}
                            >
                                {TEXT_NEW_INSPECTION}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#9499A8', mt: 0.3, fontSize: '0.78rem' }}>
                                Select a form template to begin
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                bgcolor: '#F5F6FA',
                                color: '#6B7280',
                                borderRadius: '10px',
                                '&:hover': { bgcolor: '#ECEEF2', color: '#1A1D23' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ overflowY: 'auto', flex: 1, px: 2.5, py: 2 }}>
                        {isInspectionLoading ? (
                            /* Skeleton Loading */
                            <List disablePadding>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            mb: 1.5,
                                            p: 1.5,
                                            border: '1px solid #ECEEF2',
                                            borderRadius: '12px',
                                        }}
                                    >
                                        <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '10px', flexShrink: 0 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Skeleton variant="text" width="65%" height={20} />
                                            <Skeleton variant="text" width="40%" height={16} />
                                        </Box>
                                    </Box>
                                ))}
                            </List>
                        ) : (
                            <List disablePadding>
                                {inspectionFormTemplateList?.map((form, index) => (
                                    <ListItem
                                        key={index}
                                        disablePadding
                                        sx={{ mb: 1.2 }}
                                        onClick={() => handleSectionClick(form)}
                                    >
                                        <ListItemButton
                                            sx={{
                                                border: '1px solid #ECEEF2',
                                                borderRadius: '14px',
                                                py: 1.5,
                                                px: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    backgroundColor: `${accentColor}08`,
                                                    borderColor: `${accentColor}44`,
                                                    transform: 'translateX(2px)',
                                                },
                                            }}
                                        >
                                            {/* Icon */}
                                            <Box
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '10px',
                                                    bgcolor: `${accentColor}14`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <AssignmentOutlinedIcon sx={{ fontSize: 20, color: accentColor }} />
                                            </Box>

                                            <ListItemText
                                                primary={form?.title}
                                                primaryTypographyProps={{
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    color: '#1A1D23',
                                                    noWrap: true,
                                                }}
                                            />
                                            <ArrowForwardIosIcon
                                                sx={{ fontSize: 13, color: '#BCC0CC', flexShrink: 0 }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
            </Modal>

            {openSectionModal && (
                <SectionViewModal
                    open={openSectionModal}
                    onClose={() => { handleClose(); setOpenSectionModal(false); }}
                    selectedItem={selectedItem}
                    getList={() => { handleClose(); getList(); }}
                />
            )}
        </>
    );
}